import { useGtm as useVueGtm } from '@gtm-support/vue-gtm'
import { getTranslatedProperty } from '@shopware/helpers'
import { snakeCase } from 'lodash-es'
import type { Schemas } from '#shopware'

export const useGtmEvents = () => {
  const gtm = useVueGtm()
  const { currency, selectedShippingMethod } = useSessionContext()
  const { cartItems, subtotal } = useCart()

  function trackAddToCart(product: Ref<Schemas['Product']>, quantity: number) {
    gtm?.trackEvent({ ecommerce: null }) // Clear the previous ecommerce object.
    gtm?.trackEvent({
      event: 'add_to_cart',
      ecommerce: {
        currency: currency.value?.isoCode,
        value: product.value?.calculatedPrice?.unitPrice * quantity,
        items: [
          {
            item_id: product.value?.id,
            variant: product.value?.productNumber,
            item_name: getTranslatedProperty(product.value, 'name'),
            discount: Math.abs(
              product.value?.calculatedPrice?.listPrice?.discount || 0,
            ),
            item_brand: getTranslatedProperty(
              product.value?.manufacturer,
              'name',
            ),
            item_list_id: snakeCase(
              getTranslatedProperty(product.value?.seoCategory, 'name'),
            ),
            item_list_name: getTranslatedProperty(
              product.value?.seoCategory,
              'name',
            ),
            price: product.value?.calculatedPrice?.unitPrice,
            quantity: quantity,
          },
        ],
      },
    })
  }

  function trackUpdateCartItemQuantity(product: Ref<Schemas['LineItem']>, quantity: number) {
    gtm?.trackEvent({ ecommerce: null }) // Clear the previous ecommerce object.
    gtm?.trackEvent({
      event: 'add_to_cart',
      ecommerce: {
        currency: currency.value?.isoCode,
        value: product.value?.price?.unitPrice! * quantity,
        items: [
          {
            item_id: product.value?.id,
            variant: product.value?.payload?.productNumber,
            item_name: getTranslatedProperty(product.value, 'label'),
            discount: Math.abs(product.value?.price?.listPrice?.discount || 0),
            price: product.value?.price?.unitPrice,
            quantity: quantity,
          },
        ],
      },
    })
  }

  function trackRemoveFromCart(cartItem: Schemas['LineItem']) {
    gtm?.trackEvent({ ecommerce: null }) // Clear the previous ecommerce object.
    gtm?.trackEvent({
      event: 'remove_from_cart',
      ecommerce: {
        currency: currency.value?.isoCode,
        value: cartItem?.price?.unitPrice * cartItem.quantity,
        items: [
          {
            item_id: cartItem?.id,
            variant: cartItem?.payload?.productNumber,
            item_name: getTranslatedProperty(cartItem, 'label'),
            discount: Math.abs(cartItem?.price?.listPrice?.discount || 0),
            price: cartItem?.price?.unitPrice,
            quantity: cartItem?.quantity,
          },
        ],
      },
    })
  }

  function trackViewCart() {
    gtm?.trackEvent({ ecommerce: null }) // Clear the previous ecommerce object.
    gtm?.trackEvent({
      event: 'view_cart',
      ecommerce: {
        currency: currency.value?.isoCode,
        value: subtotal.value,
        items: cartItems.value?.map((cartItem, index) => ({
          item_id: cartItem.id,
          variant: cartItem?.payload?.productNumber,
          item_name: cartItem?.label,
          discount: Math.abs(cartItem?.price?.listPrice?.discount || 0),
          index: index,
          price: cartItem?.price?.unitPrice,
          quantity: cartItem?.quantity,
        })),
      },
    })
  }

  function trackViewItemList(currentListing: Schemas["ProductListingResult"], category: Schemas["Category"] | string) {
    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: typeof category === 'string' ? category : snakeCase(getTranslatedProperty(category, 'name')),
        item_list_name: typeof category === 'string' ? category : getTranslatedProperty(category, 'name'),
        items: currentListing?.elements?.map(
          (item: Schemas['Product'], index: number) => ({
            item_id: item?.id,
            variant: item?.productNumber,
            item_name: getTranslatedProperty(item, 'name'),
            discount: Math.abs(item?.calculatedPrice?.listPrice?.discount || 0),
            index: index,
            item_brand: getTranslatedProperty(item?.manufacturer, 'name'),
            price: item?.calculatedPrice?.unitPrice,
          }),
        ),
      },
    })
  }

  function trackViewItem(product: Schemas['Product']) {
    gtm?.trackEvent({ ecommerce: null }) // Clear the previous ecommerce object.
    gtm?.trackEvent({
      event: 'view_item',
      ecommerce: {
        currency: currency.value?.isoCode,
        value: product?.calculatedPrice?.unitPrice,
        items: [
          {
            item_id: product?.id,
            variant: product?.productNumber,
            item_name: getTranslatedProperty(product, 'name'),
            discount: Math.abs(
              product?.calculatedPrice?.listPrice?.discount || 0,
            ),
            item_brand: getTranslatedProperty(
              product?.manufacturer,
              'name',
            ),
            item_list_id: snakeCase(
              getTranslatedProperty(product?.seoCategory, 'name'),
            ),
            item_list_name: getTranslatedProperty(
              product?.seoCategory,
              'name',
            ),
            price: product?.calculatedPrice?.unitPrice,
            quantity: product?.calculatedPrice?.quantity,
          },
        ],
      },
    })
  }

  function trackBeginCheckout() {
    gtm?.trackEvent({ ecommerce: null }) // Clear the previous ecommerce object.
    gtm?.trackEvent({
      event: 'begin_checkout',
      ecommerce: {
        currency: currency.value?.isoCode,
        value: subtotal.value,
        items: cartItems.value?.map((cartItem, index) => ({
          item_id: cartItem.id,
          variant: cartItem?.payload?.productNumber,
          item_name: cartItem?.label,
          discount: Math.abs(cartItem?.price?.listPrice?.discount || 0),
          index: index,
          price: cartItem?.price?.unitPrice,
          quantity: cartItem?.quantity,
        })),
      },
    })
  }

  function trackSelectItem(category: Schemas['Category'], product: Schemas['Product'], gtmItemListName?: string) {
    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'select_item',
      ecommerce: {
        item_list_id: gtmItemListName
          ? snakeCase(gtmItemListName)
          : snakeCase(getTranslatedProperty(category, 'name')),
        item_list_name:
          gtmItemListName || getTranslatedProperty(category, 'name'),
        items: [
          {
            item_id: product?.id,
            variant: product?.productNumber,
            item_name: getTranslatedProperty(product, 'name'),
            discount: Math.abs(
              product?.calculatedPrice?.listPrice?.discount || 0,
            ),
            item_brand: getTranslatedProperty(
              product?.manufacturer,
              'name',
            ),
            price: product?.calculatedPrice?.unitPrice,
          },
        ],
      },
    })
  }

  function trackShippingInfo() {
    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'add_shipping_info',
      ecommerce: {
        currency: currency.value?.isoCode,
        value: subtotal.value,
        shipping_tier: getTranslatedProperty(
          selectedShippingMethod.value,
          'name',
        ),
        items: cartItems.value?.map((cartItem, index) => ({
          item_id: cartItem.id,
          variant: cartItem?.payload?.productNumber,
          item_name: cartItem?.label,
          discount: Math.abs(cartItem?.price?.listPrice?.discount || 0),
          index: index,
          price: cartItem?.price?.unitPrice,
          quantity: cartItem?.quantity,
        })),
      },
    })
  }

  function trackPurchase(order: Schemas['Order'] | undefined | null): void {
    gtm?.trackEvent({ ecommerce: null })

		const orderCalculatedTax = order?.price?.calculatedTaxes?.[0]?.tax

		const orderPromotionCode = order?.lineItems
			?.filter((lineItem) => lineItem.type === 'promotion')
			?.map((lineItem) => lineItem?.payload?.code)
			?.join('|')

		gtm?.trackEvent({
			event: 'purchase',
			ecommerce: {
				transaction_id: order?.orderNumber,
				value: order?.price?.totalPrice,
				tax: orderCalculatedTax ?? 0,
				shipping: order?.shippingTotal,
				coupon: orderPromotionCode ?? '',
				currency: currency.value?.isoCode,
        items: order?.lineItems?.map((lineItem) => ({
          item_id: lineItem?.id,
          item_name: lineItem?.label,
          price: lineItem?.totalPrice,
          // @ts-ignore
          item_variant: lineItem?.payload?.productNumber,
          quantity: lineItem.quantity
        }))
			}
		})
	}

  function trackSignUp() {
    gtm?.trackEvent({event: 'sign_up'})
  }

  function trackLogin() {
    gtm?.trackEvent({event: 'login'})
  }

  return {
    trackAddToCart,
    trackUpdateCartItemQuantity,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackShippingInfo,
    trackSelectItem,
    trackViewItemList,
    trackViewItem,
    trackViewCart,
    trackPurchase,
    trackSignUp,
    trackLogin,
  }
}
